import { useAsync } from "@react-hookz/web";
import type { FunctionComponentWithRef } from "react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";
import { Navigation } from "swiper/modules";
import type { SwiperRef } from "swiper/react";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import type { newsletterColumnistSlideshowAppConfigQuery$key } from "scmp-app/queries/__generated__/newsletterColumnistSlideshowAppConfigQuery.graphql";
import type { newsletterColumnistSlideshowAuthorsQuery } from "scmp-app/queries/__generated__/newsletterColumnistSlideshowAuthorsQuery.graphql";

import { NewsletterColumnistItem } from "./newsletter-columnist-item";
import {
  ArrowContainer,
  ArrowNext,
  ArrowPrevious,
  Container,
  Header,
  List,
  NewBadge,
  StyledSwiper,
  StyledSwiperSlide,
} from "./styles";
import type { ScmpNewsletterColumnist } from "./types";

export type Props = React.DetailsHTMLAttributes<HTMLDivElement> & {
  reference: newsletterColumnistSlideshowAppConfigQuery$key;
};

export const NewsletterColumnistSlideshow: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  ref: forwardReference,
  reference,
  ...props
}) => {
  const appConfig = useFragment(
    graphql`
      fragment newsletterColumnistSlideshowAppConfigQuery on Query {
        newslettersColumnist: appConfig(filter: { entityId: "scmp_pwa_newsletter_columnist" }) {
          json
        }
      }
    `,
    reference,
  );

  const columnists = useMemo(
    () => (appConfig.newslettersColumnist?.json ?? []) as ScmpNewsletterColumnist[],
    [appConfig.newslettersColumnist?.json],
  );

  const environment = useRelayEnvironment();

  const [{ result: authorData = {}, status: authorDataStatus }, { execute }] = useAsync(
    async () => {
      const data = await fetchQuery<newsletterColumnistSlideshowAuthorsQuery>(
        environment,
        graphql`
          query newsletterColumnistSlideshowAuthorsQuery($entityIds: [String], $first: Int) {
            authors(filter: { entityIds: $entityIds }, first: $first) {
              edges {
                node {
                  entityId
                  ...newsletterColumnistItemAuthor
                }
              }
            }
          }
        `,
        {
          entityIds: columnists.map(columnist => columnist.entityId),
          first: columnists.length,
        },
        { fetchPolicy: "store-or-network" },
      ).toPromise();

      if (!data?.authors?.edges) return;
      return Object.fromEntries(data.authors.edges.map(({ node }) => [node.entityId, node]));
    },
  );

  useEffect(() => {
    if (authorDataStatus !== "not-executed" || !notEmpty(columnists)) return;
    void execute();
  }, [authorDataStatus, columnists, execute]);

  const swiperReference = useRef<SwiperRef>(null);
  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), false);
  const [isPreviousDisable, setIsPreviousDisable] = React.useState(true);
  const [isNextDisable, setIsNextDisable] = React.useState(false);

  const handleArrowClick = useCallback((direction: "next" | "prev") => {
    if (!swiperReference.current) return;

    switch (direction) {
      case "next":
        swiperReference.current.swiper.slideNext();
        break;
      case "prev":
        swiperReference.current.swiper.slidePrev();
        break;
    }
  }, []);

  useEffect(() => {
    if (!swiperReference.current) return;
    const instance = swiperReference.current.swiper;

    const updateButtonStates = () => {
      setIsPreviousDisable(instance.isBeginning);
      setIsNextDisable(instance.isEnd);
    };

    updateButtonStates();

    instance.on("slideChange", updateButtonStates);
    instance.on("reachBeginning", updateButtonStates);
    instance.on("reachEnd", updateButtonStates);
    instance.on("init", updateButtonStates);
    instance.on("resize", updateButtonStates);

    return () => {
      instance.off("slideChange", updateButtonStates);
      instance.off("reachBeginning", updateButtonStates);
      instance.off("reachEnd", updateButtonStates);
      instance.off("init", updateButtonStates);
      instance.off("resize", updateButtonStates);
    };
  }, []);

  if (!notEmpty(columnists)) return null;

  return (
    <Container ref={forwardReference} {...props}>
      <Header>
        Follow Alerts ({columnists.length}) <NewBadge>NEW</NewBadge>
        <ArrowContainer>
          <ArrowPrevious
            $isDisabled={isPreviousDisable || swiperReference.current?.swiper.isLocked}
            onClick={() => handleArrowClick("prev")}
          />
          <ArrowNext
            $isDisabled={isNextDisable || swiperReference.current?.swiper.isLocked}
            onClick={() => handleArrowClick("next")}
          />
        </ArrowContainer>
      </Header>

      <List>
        <StyledSwiper
          modules={[Navigation]}
          pagination={false}
          ref={swiperReference}
          slidesOffsetAfter={isTabletUp ? 0 : 20}
          slidesOffsetBefore={isTabletUp ? 0 : 20}
          slidesPerGroupAuto
          slidesPerView="auto"
          spaceBetween={20}
        >
          {columnists.map(columnist => (
            <StyledSwiperSlide key={columnist.entityId}>
              {authorData[columnist.entityId] && (
                <NewsletterColumnistItem
                  data={columnist}
                  reference={authorData[columnist.entityId]}
                />
              )}
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
      </List>
    </Container>
  );
};

NewsletterColumnistSlideshow.displayName = "NewsletterColumnistSlideshow";
